/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXAtomSetter, useStateXSetter } from '@cloudio-saas/statex';
import { Box, Button, IconButton, Theme, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Icon from 'icons/Icon';
import { camelCase } from 'lodash-es';
import React, { memo, Suspense, useEffect } from 'react';
import SimpleLoading from 'root/SimpleLoading';
import Store from 'store/Store';
import useDataStore from 'store/useDataStore';
import { useIoPagesEditStore, useIoPagesStore } from 'store/utils/utils';
import { getErrorMessage } from 'utils/isEmpty';
import { StaticRuntimeContext } from '../../../../../common/Context';
import { pageAtom } from '../../../../../designer/atoms';
import useComponentSize from '../../../../../hooks/useComponentSize';
import { usePlatform } from '../../../../../platform/PlatformHooks';
import PageContainer from '../../../../../root/PageContainer';
import {
  PageView,
  sidebarActivePageIdAtom,
  sidebarPageWizardTitleAtom,
} from '../../../atoms';
import {
  non_ds_registry,
  PageTemplate,
} from './none_ds_templates/none_ds_registry';
import { ds_registry } from './templates/ds_registry';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    bottom: 0,
    top: 0,
    left: 0,
    right: 0,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.default,
  },
  body: {
    padding: '16px',
    display: 'flex',
    flex: 1,
    overflowX: 'hidden',
    overflowY: 'auto',
    gap: '16px',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    justifyItems: 'flex-start',
    placeItems: 'flex-start',
    alignContent: 'flex-start',
  },
}));

const PageWizardChooseTemplate = memo(
  ({
    setView,
    appUid,
    store,
  }: {
    appUid: string;
    setView: (_view: PageView) => void;
    store?: Store<unknown> | null;
  }): React.ReactElement | null => {
    const classes = useStyles();
    const platform = usePlatform();
    const setTitle = useStateXAtomSetter(sidebarPageWizardTitleAtom);
    const set = useStateXSetter();
    const [registry, setRegistry] = React.useState<PageTemplate[]>(() => []);
    const listStore = useIoPagesStore(appUid);
    const editStore = useIoPagesEditStore(appUid);

    const s2 = useComponentSize();

    const ref = React.useRef({
      appUid,
      platform,
      set,
      store,
      counter: 0,
    });

    useEffect(() => {
      ref.current.counter++;
      if (store) {
        const pageIds: string[] = [];
        async function process(_store: Store<unknown>): Promise<void> {
          const reg: (PageTemplate | null)[] = await Promise.all(
            ds_registry.map(
              async ({
                generatePage,
                name,
                pageId: testId,
              }): Promise<PageTemplate | null> => {
                const pageId = `${_store.ds}-${testId}`;
                pageIds.push(pageId);
                try {
                  const page = await generatePage({
                    platform,
                    store: _store,
                    appUid,
                    pageId,
                  });
                  set(pageAtom, page, { params: { appUid, pageId } });
                  return {
                    name,
                    height: window.innerHeight - 82,
                    pageId,
                    page,
                  };
                } catch (e) {
                  const msg = getErrorMessage(e);
                  platform.showError(msg);
                }
                return null;
              },
            ),
          );
          setRegistry(reg.filter((t) => !!t) as PageTemplate[]);
        }
        process(store).catch((e) => {
          console.error(e);
        });
        return () => {
          pageIds.forEach((pageId) => {
            set(pageAtom, undefined, { params: { appUid, pageId } });
          });
        };
      } else {
        setRegistry(non_ds_registry);
        non_ds_registry.forEach(({ page, pageId }) => {
          set(pageAtom, page, { params: { appUid, pageId } });
        });
        return () => {
          non_ds_registry.forEach(({ pageId }) => {
            set(pageAtom, undefined, { params: { appUid, pageId } });
          });
        };
      }
    }, [appUid, platform, set, store]);
    const [previewPageId, setPreviewPageId] = React.useState('');

    return (
      <div className={classes.root}>
        <div className={classes.body} ref={s2.ref}>
          {registry
            .filter((t) => !!t)
            .map((t, i) => (
              <Box
                key={t.name}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  width: '100%',
                  whiteSpace: 'nowrap',
                  flexWrap: 'nowrap',
                }}
              >
                <Box
                  key={t.name}
                  sx={{
                    display: 'flex',
                    gap: '8px',
                    width: '100%',
                    flexWrap: 'nowrap',
                  }}
                >
                  <Typography
                    variant="h6"
                    textOverflow="ellipsis"
                    overflow="hidden"
                    whiteSpace="nowrap"
                    flex="1"
                  >
                    {i + 1}. {t.name}
                  </Typography>
                  <IconButton
                    data-testid={`preview-${t.pageId}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setPreviewPageId(
                        t.pageId === previewPageId ? '' : t.pageId,
                      );
                    }}
                    data-tip={
                      t.pageId === previewPageId
                        ? 'Hide Preview'
                        : 'Preview Page'
                    }
                  >
                    <Icon
                      iconName={
                        t.pageId === previewPageId ? 'eye-slash' : 'eye'
                      }
                    />
                  </IconButton>
                  <Button
                    data-testid={`create-${t.pageId}`}
                    variant="contained"
                    onClick={async () => {
                      const page = t.page;
                      set(sidebarActivePageIdAtom, '');
                      await editStore.clear();
                      await editStore.createNew({
                        partialRecord: {
                          appUid,
                          md: page,
                          showInNavigationMenu: 'Y',
                        },
                        rs: 'I',
                      });
                      await listStore.createNew({
                        partialRecord: {
                          appUid,
                          showInNavigationMenu: 'Y',
                        },
                        rs: 'I',
                      });
                      setTitle('Give a name to this page');
                      setView('edit');
                    }}
                  >
                    Choose
                  </Button>
                </Box>
                {t.pageId === previewPageId ? (
                  <StaticRuntimeContext.Provider value>
                    <Suspense fallback={<SimpleLoading name={t.pageId} />}>
                      <PageContainer
                        key={t.pageId}
                        width={s2.width - 32}
                        height={t.height ?? 600}
                        appUid={appUid}
                        pageId={t.pageId}
                        roleUid="developer"
                        paramString=""
                        embedded
                      />
                    </Suspense>
                  </StaticRuntimeContext.Provider>
                ) : null}
              </Box>
            ))}
        </div>
      </div>
    );
  },
);

export const PageWizardChooseTemplateWithStore = memo(
  ({
    setView,
    appUid,
    ds,
  }: {
    appUid: string;
    setView: (_view: PageView) => void;
    ds: string;
  }): React.ReactElement | null => {
    const store = useDataStore<unknown>({
      appUid,
      pageId: ds,
      ds,
      alias: camelCase(`${ds}Alias`),
    });
    return (
      <PageWizardChooseTemplate
        setView={setView}
        appUid={appUid}
        store={store}
      />
    );
  },
);

export default PageWizardChooseTemplate;
